/**
 * Version History modal (RT-VERSIONING) — the GitHub commit timeline for a
 * single synced upload/capture, reached from the Clock affordance on an
 * AssetLocker / Captures entry that carries a `githubPath`.
 *
 * Deploy-gated (🚀): with no GitHub PAT the timeline is empty
 * (`listCommitsForPath` returns `[]`) and the modal renders a clear
 * "not connected" state — the local-first posture. A real dev PAT (no
 * Azure) populates it; that's the DEP-VERSIONING verification.
 *
 * Uploads/captures only — full record versioning needs the deferred
 * JSON-record mirror.
 */

import { useEffect, useState } from "react";
import { Clock, Download, History, RotateCcw, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { toast } from "@/hooks/use-toast";
import {
  getBlobAtCommit,
  isGithubSyncEnabled,
  listCommitsForPath,
  uploadBlob,
  uploadCapture,
  type GithubCommit,
} from "@/lib/github-storage";
import { useAssetLocker } from "@/stores/useAssetLocker";
import { useCaptures } from "@/stores/useCaptures";
import { useVersionHistory, type VersionTarget } from "./useVersionHistory";

type VersionHistoryModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Basename of a repo-rooted path (`workshops/WS4/uploads/x.png` → `x.png`). */
function fileName(path: string): string {
  return path.split("/").pop() ?? path;
}

/** Browser-side download of a Blob under `name`. */
function triggerDownload(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function VersionHistoryModal({
  open,
  onOpenChange,
}: VersionHistoryModalProps) {
  const target = useVersionHistory((s) => s.target);

  const [commits, setCommits] = useState<GithubCommit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-commit in-flight guard so a slow view/restore can't double-fire.
  const [busySha, setBusySha] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !target) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCommits([]);
    void listCommitsForPath(target.path)
      .then((list) => {
        if (!cancelled) setCommits(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Couldn’t load history.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, target]);

  async function handleView(t: VersionTarget, commit: GithubCommit): Promise<void> {
    setBusySha(commit.sha);
    try {
      const blob = await getBlobAtCommit(t.path, commit.sha);
      if (!blob) {
        toast({ title: "Nothing to view", description: "That version is empty." });
        return;
      }
      const typed = t.mimeType ? new Blob([blob], { type: t.mimeType }) : blob;
      triggerDownload(typed, fileName(t.path));
    } catch (err) {
      toast({
        title: "View failed",
        description: err instanceof Error ? err.message : "Couldn’t fetch that version.",
      });
    } finally {
      setBusySha(null);
    }
  }

  async function handleRestore(t: VersionTarget, commit: GithubCommit): Promise<void> {
    setBusySha(commit.sha);
    try {
      const blob = await getBlobAtCommit(t.path, commit.sha);
      if (!blob) {
        toast({ title: "Nothing to restore", description: "That version is empty." });
        return;
      }
      const typed = t.mimeType ? new Blob([blob], { type: t.mimeType }) : blob;
      const name = fileName(t.path);
      // Re-upload the historical bytes as a new HEAD commit (pass the
      // current SHA so GitHub updates the file in place). Captures vs
      // uploads live in parallel trees.
      if (t.kind === "capture") {
        await uploadCapture(t.workshopId, name, typed, t.sha);
        void useCaptures.getState().sync();
      } else {
        await uploadBlob(t.workshopId, name, typed, t.sha);
        void useAssetLocker.getState().sync();
      }
      toast({
        title: "Version restored",
        description: `${t.label} reset to the ${commit.sha.slice(0, 7)} version.`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Restore failed",
        description: err instanceof Error ? err.message : "Couldn’t restore that version.",
      });
    } finally {
      setBusySha(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none", width: "70vw", maxWidth: 640, maxHeight: "80vh" }}
      >
        <DialogTitle className="sr-only">Version History</DialogTitle>
        <DialogDescription className="sr-only">
          Version history{target ? ` for ${target.label}` : ""}
        </DialogDescription>

        <div className="modal-shell vh-modal" style={{ width: "100%", maxHeight: "80vh" }}>
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-[10px]">
              <History
                aria-hidden
                style={{ width: 14, height: 14, color: "var(--accent-true-blue)" }}
              />
              <span className="modal-title">VERSION HISTORY</span>
              {target && (
                <span
                  style={{
                    padding: "1px 8px",
                    background: "rgba(var(--accent-true-blue-rgb), 0.10)",
                    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.30)",
                    borderRadius: 10,
                    fontSize: "var(--fs-label)",
                    color: "var(--accent-true-blue)",
                    fontWeight: 600,
                    letterSpacing: 1,
                    maxWidth: 280,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {target.label}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="modal-close-btn"
              aria-label="Close"
            >
              <X aria-hidden style={{ width: 12, height: 12 }} />
              CLOSE
            </button>
          </div>

          {/* Body */}
          <div className="modal-body" style={{ padding: 16 }}>
            {loading ? (
              <p style={emptyTextStyle}>Loading version history…</p>
            ) : error ? (
              <p style={{ ...emptyTextStyle, color: "var(--accent-red)" }}>{error}</p>
            ) : commits.length === 0 ? (
              <p style={emptyTextStyle}>
                {isGithubSyncEnabled()
                  ? "No version history found for this entry yet."
                  : "Version history isn’t available for this storage backend yet."}
              </p>
            ) : (
              <ul
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {commits.map((commit, i) => (
                  <li
                    key={commit.sha}
                    className="vh-row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: "1px solid hsl(var(--border))",
                      background: "var(--bg-panel-alt)",
                    }}
                  >
                    <Clock
                      aria-hidden
                      style={{
                        width: 14,
                        height: 14,
                        flexShrink: 0,
                        color: i === 0 ? "var(--accent-green)" : "var(--text-muted)",
                      }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: "var(--fs-base)",
                          color: "var(--text-primary)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {commit.message || "(no message)"}
                        {i === 0 && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: "var(--fs-label)",
                              color: "var(--accent-green)",
                              letterSpacing: 1,
                            }}
                          >
                            CURRENT
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "var(--fs-label)",
                          color: "var(--text-muted)",
                          letterSpacing: 0.5,
                        }}
                      >
                        {commit.author}
                        {commit.date ? ` · ${new Date(commit.date).toLocaleString()}` : ""}
                        {` · ${commit.sha.slice(0, 7)}`}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        type="button"
                        disabled={!!busySha}
                        onClick={() => target && void handleView(target, commit)}
                        className="vh-action"
                        title="Download this version"
                        aria-label={`Download the ${commit.sha.slice(0, 7)} version`}
                        style={actionBtnStyle}
                      >
                        <Download aria-hidden style={{ width: 12, height: 12 }} />
                        View
                      </button>
                      {i !== 0 && (
                        <button
                          type="button"
                          disabled={!!busySha}
                          onClick={() => target && void handleRestore(target, commit)}
                          className="vh-action"
                          title="Restore this version as the current one"
                          aria-label={`Restore the ${commit.sha.slice(0, 7)} version`}
                          style={actionBtnStyle}
                        >
                          <RotateCcw aria-hidden style={{ width: 12, height: 12 }} />
                          Restore
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Versioning · uploads &amp; captures</span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

const emptyTextStyle = {
  textAlign: "center" as const,
  color: "var(--text-secondary)",
  fontSize: "var(--fs-sub)",
  lineHeight: 1.6,
  padding: "32px 0",
};

const actionBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 9px",
  borderRadius: 7,
  border: "1px solid hsl(var(--border))",
  background: "transparent",
  color: "var(--text-secondary)",
  fontSize: "var(--fs-label)",
  letterSpacing: 1,
  cursor: "pointer",
};
